import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { MessageSquare, AlertCircle } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useHapticFeedback } from '@/hooks/useHapticFeedback';

interface PromptModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSubmit: (value: string) => void;
    title: string;
    message: string;
    placeholder?: string;
    defaultValue?: string;
    confirmText?: string;
    cancelText?: string;
    isLoading?: boolean;
    inputType?: string;
    required?: boolean;
}

export function PromptModal({
    isOpen,
    onClose,
    onSubmit,
    title,
    message,
    placeholder = 'Enter value...',
    defaultValue = '',
    confirmText = 'Submit',
    cancelText = 'Cancel',
    isLoading = false,
    inputType = 'text',
    required = true
}: PromptModalProps) {
    const { impactOccurred } = useHapticFeedback();
    const [value, setValue] = useState(defaultValue);
    const [error, setError] = useState(false);

    useEffect(() => {
        if (isOpen) {
            setValue(defaultValue);
            setError(false);
        }
    }, [isOpen, defaultValue]);

    if (!isOpen) return null;

    const handleSubmit = () => {
        if (required && !value.trim()) {
            impactOccurred('heavy');
            setError(true);
            return;
        }
        impactOccurred('medium');
        onSubmit(value);
    };

    return (
        <AnimatePresence>
            <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 sm:p-6 pb-20 sm:pb-6">
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className="absolute inset-0 bg-black/60 backdrop-blur-sm"
                    onClick={() => !isLoading && onClose()}
                />
                <motion.div
                    initial={{ opacity: 0, scale: 0.95, y: 20 }}
                    animate={{ opacity: 1, scale: 1, y: 0 }}
                    exit={{ opacity: 0, scale: 0.95, y: 20 }}
                    className="relative w-full max-w-sm bg-[#1A1A1A] border border-white/10 rounded-[24px] p-6 shadow-2xl overflow-hidden"
                >
                    <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[200px] h-[100px] blur-[60px] opacity-20 pointer-events-none rounded-full bg-[#B4F73D]" />

                    <div className="relative z-10 w-full flex flex-col items-center">
                        <div className="w-12 h-12 rounded-full flex items-center justify-center border bg-[#B4F73D]/10 border-[#B4F73D]/30 text-[#B4F73D] mb-4">
                            <MessageSquare size={24} />
                        </div>

                        <h3 className="text-xl font-black text-center text-white mb-2">{title}</h3>
                        <p className="text-gray-400 text-sm text-center mb-6 whitespace-pre-wrap">{message}</p>

                        <div className="w-full relative mb-8">
                            <input
                                autoFocus
                                type={inputType}
                                value={value}
                                onChange={(e) => {
                                    setValue(e.target.value);
                                    if (error) setError(false);
                                }}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter') handleSubmit();
                                }}
                                placeholder={placeholder}
                                className={cn(
                                    "w-full bg-black/40 border border-white/10 text-white rounded-xl px-4 py-3.5 focus:outline-none focus:ring-2 focus:ring-[#B4F73D]/50 transition-all placeholder:text-gray-600",
                                    error && "border-red-500/50 focus:ring-red-500/50 animate-shake"
                                )}
                            />
                            {error && (
                                <div className="absolute -bottom-5 left-1 flex items-center gap-1 text-red-400 text-[10px] mt-1">
                                    <AlertCircle size={10} />
                                    <span>This field is required</span>
                                </div>
                            )}
                        </div>

                        <div className="flex gap-3 w-full">
                            <button
                                onClick={() => {
                                    impactOccurred('light');
                                    onClose();
                                }}
                                disabled={isLoading}
                                className="flex-1 py-3.5 px-4 rounded-xl font-bold bg-white/5 text-white/70 hover:bg-white/10 transition-colors border border-white/10 disabled:opacity-50"
                            >
                                {cancelText}
                            </button>
                            <button
                                onClick={handleSubmit}
                                disabled={isLoading}
                                className="flex-1 py-3.5 px-4 rounded-xl font-bold bg-[#B4F73D] text-[#1A1A1A] hover:bg-[#9DE02A] transition-all flex justify-center items-center shadow-lg shadow-[#B4F73D]/20 active:scale-95 disabled:opacity-50"
                            >
                                {isLoading ? (
                                    <div className="w-5 h-5 border-2 border-[#1A1A1A]/30 border-t-[#1A1A1A] rounded-full animate-spin" />
                                ) : confirmText}
                            </button>
                        </div>
                    </div>
                </motion.div>
            </div>
        </AnimatePresence>
    );
}
